// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Segment } from "semantic-ui-react";
import _ from "lodash";
import * as outlierFunctions from "../../../Lumber/DataAnalysisCriteria/outlierFunctions";
import AFPlot from "../../../Lumber/Plotting/AFPlotly";
import { BOX_LOWER_FILL_COLOR, BOX_UPPER_FILL_COLOR, TRANSPARENT_COLOR } from "../../../../utilities/graphics";

const AucBoxWhiskerChart = ({
	data,
	allData,
	analysisName,
	meanComparison,
	alphaValue,
	runMeanComparison,
	needToUpdateAnalytics,
	isInitialLoad,
	selectedAnalysisOption,
	groundTrialDataChecked,
	initialGroupTrialDataCheckedCount,
	initialFlightDataCheckedCount,
	flightDataChecked,
	needToRerunStats,
	timeSeriesPresets,
	setIsBoxWhiskerChartLoading,
	boxWhiskerMeanComparisonDataAuc,
	boxWhiskerAllMeanComparisonDataAuc,
	selectedAnalysisType,
	disableColors,
}) => {
	const entityNames = useSelector((state) => state.entityNames ?? null);

	//-- Data
	const [meanComparisonId, setMeanComparisonId] = useState(null);
	const [alphaId, setAlphaId] = useState(null);
	const [chartOptions, setChartOptions] = useState({ data: [], layout: {} });
	const [noData, setNoData] = useState(true);

	//-- UI Control
	const [chartLoading, setChartLoading] = useState(true);

	const colors = !disableColors
		? [
				"#920000",
				"#006DDB",
				"#B66DFF",
				"#24FF24",
				"#FFFF6D",
				"#004949",
				"#009292",
				"#FF6DB6",
				"#FFB6DB",
				"#490092",
				"#924900",
				"#DB6D00",
				"#6DB6FF",
				"#B6DBFF",
				"#000000",
			]
		: ["#920000"];
	const shapes = ["circle"];
	let colorIdx = 0;
	let shapeIdx = 0;

	useEffect(() => {
		if (meanComparison && !needToUpdateAnalytics) {
			setMeanComparisonId(meanComparison);
		}
	}, [meanComparison]);

	useEffect(() => {
		if (alphaValue && !needToUpdateAnalytics) {
			setAlphaId(alphaValue);
		}
	}, [alphaValue]);

	useEffect(() => {
		if (!_.isEmpty(boxWhiskerMeanComparisonDataAuc) && isInitialLoad) {
			stageChartOptions(boxWhiskerMeanComparisonDataAuc);
		}
	}, [boxWhiskerMeanComparisonDataAuc, disableColors]);

	useEffect(() => {
		if (data.length > 0) {
			setNoData(false);
			if (runMeanComparison === true && meanComparisonId && alphaId) {
				let tempAnalysisTypeId = _.find(timeSeriesPresets, (rpo) => rpo.name === analysisName)?.analysisTypeId;
				tempAnalysisTypeId = tempAnalysisTypeId ?? selectedAnalysisType;

				let filteredFooterData = _.filter(boxWhiskerAllMeanComparisonDataAuc, (ar) => {
					return ar[0] === selectedAnalysisOption && ar[1] === tempAnalysisTypeId;
				});
				if (!_.isEmpty(filteredFooterData)) {
					let chartData = filteredFooterData[0];
					stageChartOptions(chartData[2]);
				} else {
					stageChartOptions(null);
				}
			} else {
				if (_.isEmpty(boxWhiskerMeanComparisonDataAuc) || boxWhiskerMeanComparisonDataAuc === null) {
					stageChartOptions(null);
				}
			}
		} else {
			setNoData(true);
			setChartLoading(false);
			setIsBoxWhiskerChartLoading(false);
		}
	}, [data, meanComparisonId, alphaId]);

	//Use Effect to swap chart data based on assessment selection
	useEffect(() => {
		if (alphaValue && meanComparison) {
			if (!_.isEmpty(boxWhiskerAllMeanComparisonDataAuc)) {
				let tempAnalysisTypeId = _.find(timeSeriesPresets, (rpo) => rpo.name === analysisName)?.analysisTypeId;
				tempAnalysisTypeId = tempAnalysisTypeId ?? selectedAnalysisType;

				let filteredFooterData = _.filter(boxWhiskerAllMeanComparisonDataAuc, (ar) => {
					return ar[0] === selectedAnalysisOption && ar[1] === tempAnalysisTypeId;
				});
				if (!_.isEmpty(filteredFooterData)) {
					let chartData = filteredFooterData[0];
					stageChartOptions(chartData[2]);
				}
			}
		}
	}, [groundTrialDataChecked, alphaValue, meanComparison]);

	const getNextMarker = () => {
		const marker = { color: colors[colorIdx], shape: shapes[shapeIdx] };
		if (colorIdx + 1 >= colors.length) {
			colorIdx = 0;
			//shapeIdx++;
		} else {
			colorIdx++;
		}
		return marker;
	};

	function stageChartOptions(results) {
		let treatmentIds = [];
		_.filter(groundTrialDataChecked, (item, index) => {
			if (!item) {
				treatmentIds.push(index + 1);
			}
		});

		let filteredAllData = [];
		let filteredAllDataByAnalysisType = [];
		let tempAnalysisTypeId = _.find(timeSeriesPresets, (rpo) => rpo.name === analysisName)?.analysisTypeId;
		tempAnalysisTypeId = tempAnalysisTypeId ?? selectedAnalysisType;

		filteredAllDataByAnalysisType = _.filter(allData, (ar) => {
			return ar.analysisTypeId === tempAnalysisTypeId;
		});

		_.filter(filteredAllDataByAnalysisType, (ad) => {
			_.filter(ad, (a) => {
				if (a.analysisId === selectedAnalysisOption) {
					filteredAllData.push(a);
				}
			});
		});

		let uniqueTreatmentIds = _.uniqBy(filteredAllData.length > 0 ? filteredAllData : data, "trialTreatmentId");
		uniqueTreatmentIds = _.map(uniqueTreatmentIds, (uti) => uti.trialTreatmentId);

		data = _.filter(filteredAllData.length > 0 ? filteredAllData : data, (res) => {
			return !_.includes(treatmentIds, res.trialTreatmentId);
		});

		let isAnyTreatmentDeselected = false;
		let isAnyFlightDeselected = false;

		let checkedTreatmentCountUpdated = _.map(groundTrialDataChecked, (gtdc) => gtdc === true);
		let checkedFlightsCountUpdated = _.map(flightDataChecked, (fdc) => fdc.checked === true);

		if (
			!_.isEqual(initialFlightDataCheckedCount, checkedFlightsCountUpdated) &&
			initialFlightDataCheckedCount.length !== 0 &&
			initialFlightDataCheckedCount.length === checkedFlightsCountUpdated.length
		) {
			isAnyFlightDeselected = true;
		} else {
			if (
				_.some(flightDataChecked, (ut) => !ut.checked) &&
				!_.isEqual(initialFlightDataCheckedCount, checkedFlightsCountUpdated)
			) {
				isAnyFlightDeselected = true;
			} else {
				isAnyFlightDeselected = false;
			}
		}

		if (
			!_.isEqual(initialGroupTrialDataCheckedCount, checkedTreatmentCountUpdated) &&
			initialGroupTrialDataCheckedCount.length !== 0
		) {
			isAnyTreatmentDeselected = true;
		}

		let maxTreatmentId = 0;
		if (data[0]?.maxTreatmentId == 0) {
			let temp = _.maxBy(data, "trialTreatmentId");
			maxTreatmentId = temp.trialTreatmentId;
		} else {
			maxTreatmentId = data[0]?.maxTreatmentId;
		}
		if (maxTreatmentId === undefined) {
			maxTreatmentId = _.max(uniqueTreatmentIds);
		}

		let groupedTreatments = _.groupBy(data, "trialTreatmentId");
		let scatterPlots = {};
		let boxX = [];
		let boxMin = [],
			boxQ1 = [],
			boxMedian = [],
			boxQ3 = [],
			boxMax = [];

		for (let i = 1; i < maxTreatmentId + 1; i++) {
			if (_.map(data, "trialTreatmentId").includes(i) && !isAnyFlightDeselected) {
				let areas = _.map(groupedTreatments[i], (t) => t.totalAuc);
				const median = outlierFunctions.getMedian(areas);
				const q1 = outlierFunctions.getQ1(areas);
				const q3 = outlierFunctions.getQ3(areas);
				const min = Number(Math.min(...areas).toFixed(5));
				const max = Number(Math.max(...areas).toFixed(5));

				boxX.push(String(i));
				boxMin.push(min);
				boxQ1.push(q1);
				boxMedian.push(median);
				boxQ3.push(q3);
				boxMax.push(max);

				_.map(groupedTreatments[i], (treatment) => {
					if (!scatterPlots[treatment.plotReplicate]) scatterPlots[treatment.plotReplicate] = [];
					scatterPlots[treatment.plotReplicate].push({
						x: String(i),
						y: treatment.totalAuc,
						plotName: treatment.plotName,
						treatmentNum: i,
					});
				});
			}
		}

		const statsMap = {};
		boxX.forEach((cat, idx) => {
			statsMap[cat] = { min: boxMin[idx], q1: boxQ1[idx], median: boxMedian[idx], q3: boxQ3[idx], max: boxMax[idx] };
		});

		let letterAnnotations = [];
		if (results && !isAnyFlightDeselected && !needToRerunStats && !_.isEmpty(data)) {
			let tempLetteringPairs = _.filter(results.letteringPairs, (res) => {
				return !_.includes(treatmentIds, res.treatmentId);
			});
			tempLetteringPairs = _.filter(tempLetteringPairs, (res) => {
				return _.includes(uniqueTreatmentIds, res.treatmentId);
			});

			for (let i = 1; i < maxTreatmentId + 1; i++) {
				if (_.findIndex(tempLetteringPairs, { treatmentId: i }) === -1) {
					tempLetteringPairs.push({ treatmentId: i, letter: null });
				}
			}

			let sortedLetters = _.orderBy(tempLetteringPairs, ["treatmentId"], ["asc"]);

			if (initialGroupTrialDataCheckedCount.length === 0) {
				isAnyTreatmentDeselected = _.some(sortedLetters, (sl) => sl.letter === null);
			}

			const allLettersAreA = _.map(sortedLetters, "letter").every((l) => l === "a" || l === null);

			sortedLetters.forEach(({ treatmentId, letter }) => {
				const xIdx = boxX.indexOf(String(treatmentId));
				if (xIdx === -1) return;
				letterAnnotations.push({
					x: xIdx,
					y: 1.06,
					yref: "paper",
					text: isAnyTreatmentDeselected
						? " "
						: allLettersAreA && letter !== null
							? "-"
							: letter === null
								? " "
								: letter,
					showarrow: false,
					textangle: -45,
					font: { size: 14 },
				});
			});
		} else {
			boxX.forEach((_, xIdx) => {
				letterAnnotations.push({ x: xIdx, y: 1.06, yref: "paper", text: " ", showarrow: false, font: { size: 14 } });
			});
		}

		const numTreatments = letterAnnotations.length;
		const maxLabelLen = Math.max(1, ...letterAnnotations.map((a) => (a.text ?? "").trim().length));
		const tooMany = maxLabelLen * numTreatments > 140;
		if (tooMany) {
			letterAnnotations.forEach((a) => {
				a.text = "-";
			});
		}
		const annotationY = tooMany ? 1.06 : 1.06 + Math.max(0, (maxLabelLen - 2) * 0.015);
		const marginTop = tooMany ? 90 : 90 + Math.max(0, (maxLabelLen - 2) * 10);
		letterAnnotations.forEach((a) => {
			a.y = annotationY;
		});

		// Box is never thinner than a scatter marker (8px) at the minimum expected chart width (600px)
		const scatterDataWidth = (8 / 600) * boxX.length;
		const boxWidth = Math.min(0.6, Math.max(0.2, scatterDataWidth));

		const boxBaseProps = {
			type: "box",
			x: boxX,
			boxpoints: false,
			showlegend: false,
			width: boxWidth,
		};

		const traces = [
			{
				...boxBaseProps,
				lowerfence: boxMin,
				q1: boxQ1,
				median: boxQ1,
				q3: boxMedian,
				upperfence: boxMedian,
				fillcolor: BOX_LOWER_FILL_COLOR,
				line: { color: TRANSPARENT_COLOR, width: 0 },
				hoverinfo: "skip",
			},
			{
				...boxBaseProps,
				lowerfence: boxMedian,
				q1: boxMedian,
				median: boxMedian,
				q3: boxQ3,
				upperfence: boxMax,
				fillcolor: BOX_UPPER_FILL_COLOR,
				line: { color: TRANSPARENT_COLOR, width: 0 },
				hoverinfo: "skip",
			},
			{
				...boxBaseProps,
				lowerfence: boxMin,
				q1: boxQ1,
				median: boxMedian,
				q3: boxQ3,
				upperfence: boxMax,
				fillcolor: TRANSPARENT_COLOR,
				line: { color: "black" },
				hoverinfo: "skip",
			},
		];

		for (let [rep, sp] of Object.entries(scatterPlots)) {
			const marker = getNextMarker();
			traces.push({
				type: "scatter",
				mode: "markers",
				name: `${rep}`,
				x: sp.map((p) => p.x),
				y: sp.map((p) => p.y),
				customdata: sp.map((p) => {
					const s = statsMap[p.x];
					return [p.plotName, p.treatmentNum, rep, s.min, s.q1, s.median, s.q3, s.max];
				}),
				hovertemplate:
					"<b>Plot: </b>%{customdata[0]}<br>" +
					"<b>Treatment: </b>%{customdata[1]}<br>" +
					"<b>Replicate: </b>%{customdata[2]}<br>" +
					"<b>Value: </b>%{y}<br>" +
					"<br>" +
					"<b>Min: </b>%{customdata[3]}<br>" +
					"<b>Q1: </b>%{customdata[4]}<br>" +
					"<b>Median: </b>%{customdata[5]}<br>" +
					"<b>Q3: </b>%{customdata[6]}<br>" +
					"<b>Max: </b>%{customdata[7]}<extra></extra>",
				marker: {
					color: marker.color,
					symbol: marker.shape,
					size: 8,
					line: { color: "black", width: 0.5 },
				},
				showlegend: !disableColors,
			});
		}

		const layout = {
			xaxis: {
				title: { text: "Treatment", standoff: 5 },
				type: "category",
				tickangle: 0,
				automargin: true,
				range: [-0.3, boxX.length - 0.7],
			},
			yaxis: { title: { text: "AUC " + analysisName, standoff: 10 }, zeroline: false, automargin: true },
			legend: {
				orientation: "h",
				title: { text: "Replicates" },
				x: 1,
				xanchor: "right",
				y: -0.2,
				yanchor: "top",
			},
			margin: { l: 60, r: 20, b: 80, t: marginTop, pad: 0 },
			hovermode: "closest",
			autosize: true,
			annotations: letterAnnotations,
			boxmode: "overlay",
		};

		setChartOptions({ data: traces, layout });
		setChartLoading(false);
		setIsBoxWhiskerChartLoading(false);
	}

	return (
		<Segment
			basic
			loading={chartLoading}
			style={{
				borderRadius: "8px",
				border: "1px solid var(--border)",
				background: "#ffffffe6",
				margin: 0,
				position: "relative",
			}}
		>
			{noData ? (
				<p>There is no data selected for the box and whisker chart.</p>
			) : (
				<AFPlot
					data={chartOptions.data}
					layout={chartOptions.layout}
					config={{
						toImageButtonOptions: {
							filename: `${entityNames?.trialName}_${/\(([^)]+)\)/.exec(analysisName)?.[1] ?? analysisName}_BoxWhisker`,
						},
					}}
					useResizeHandler
					style={{ width: "100%", height: "400px" }}
				/>
			)}
		</Segment>
	);
};

AucBoxWhiskerChart.propTypes = {
	data: PropTypes.array,
	allData: PropTypes.array,
	analysisName: PropTypes.string,
	meanComparison: PropTypes.string,
	alphaValue: PropTypes.string,
	runMeanComparison: PropTypes.bool,
	needToUpdateAnalytics: PropTypes.bool,
	isInitialLoad: PropTypes.bool,
	selectedAnalysisOption: PropTypes.string,
	groundTrialDataChecked: PropTypes.array,
	initialGroupTrialDataCheckedCount: PropTypes.array,
	initialFlightDataCheckedCount: PropTypes.array,
	flightDataChecked: PropTypes.array,
	needToRerunStats: PropTypes.bool,
	timeSeriesPresets: PropTypes.array,
	setIsBoxWhiskerChartLoading: PropTypes.func,
	boxWhiskerMeanComparisonDataAuc: PropTypes.array,
	boxWhiskerAllMeanComparisonDataAuc: PropTypes.array,
	selectedAnalysisType: PropTypes.string,
	disableColors: PropTypes.bool,
};
export default AucBoxWhiskerChart;
